import { useCallback, useEffect, useRef } from 'react';

export interface UseScrollManagerReturn {
  /** 绑定到聊天区域的 ref */
  readonly chatAreaRef: React.MutableRefObject<HTMLDivElement | null>;
  /** 列表底部的占位 ref */
  readonly endOfListRef: React.MutableRefObject<HTMLDivElement | null>;
  /** 滚动到底部（force=true 忽略用户滚动位置）*/
  readonly scrollToBottom: (force?: boolean) => void;
}

export const useScrollManager = (): UseScrollManagerReturn => {
  const chatAreaRef = useRef<HTMLDivElement | null>(null);
  const endOfListRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUserAtBottomRef = useRef<boolean>(true);

  const checkIfUserAtBottom = useCallback((): boolean => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return true;
    const THRESHOLD = 100;
    const distanceFromBottom =
      chatArea.scrollHeight - chatArea.scrollTop - chatArea.clientHeight;
    return distanceFromBottom <= THRESHOLD;
  }, []);

  const scrollToBottom = useCallback((force = false): void => {
    if (!force && !isUserAtBottomRef.current) return;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      chatAreaRef.current?.scrollTo({
        top: chatAreaRef.current.scrollHeight,
        // 当force为true时，直接跳转到底部，无动画效果
        behavior: force ? 'auto' : 'smooth'
      });
    }, 50);
  }, []);

  // 监听滚动事件，更新"用户是否在底部"
  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return;
    const handleScroll = (): void => {
      isUserAtBottomRef.current = checkIfUserAtBottom();
    };
    chatArea.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      chatArea.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [checkIfUserAtBottom]);

  return { chatAreaRef, endOfListRef, scrollToBottom };
};